const BASE = '/api';
const TOKEN_KEY = 'signage_token';

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(method: string, path: string, body: unknown = null): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const opts: RequestInit = { method, headers };
  if (body !== null) opts.body = JSON.stringify(body);

  const res = await fetch(BASE + path, opts);

  // 401 на auth-эндпоинтах — «неверные креды», ожидаемая ошибка: пробрасываем,
  // LoginView покажет текст. Глобальную «протухшую сессию» (чистка токена +
  // редирект) применяем только к НЕ-auth запросам.
  if (res.status === 401) {
    const isAuthEndpoint = path.startsWith('/auth/');
    if (!isAuthEndpoint) {
      setToken(null);
      if (location.pathname !== '/login') location.assign('/login');
    }
    let d: unknown = 'Unauthorized';
    try { d = (await res.json()).detail ?? 'Unauthorized'; } catch { /* нет тела */ }
    throw new ApiError(typeof d === 'string' ? d : 'Unauthorized', 401, d);
  }

  if (!res.ok) {
    let detail: unknown = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail ?? data;
    } catch { /* тело не JSON */ }
    const msg = typeof detail === 'string'
      ? detail
      : (detail as { message?: string })?.message ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(BASE + path, { method: 'POST', headers, body: form });

  if (res.status === 401) {
    const isAuthEndpoint = path.startsWith('/auth/');
    if (!isAuthEndpoint) {
      setToken(null);
      if (location.pathname !== '/login') location.assign('/login');
    }
    throw new ApiError('Unauthorized', 401, null);
  }
  if (!res.ok) {
    let detail: unknown = `HTTP ${res.status}`;
    try { const data = await res.json(); detail = data.detail ?? data; } catch { /* не JSON */ }
    const msg = typeof detail === 'string' ? detail : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? null),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? null),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? null),
  delete: <T>(path: string) => request<T>('DELETE', path),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
};
